import type { JsonObject, RefResolver } from "./refs"
import type { SchemaNode } from "./types"
import { asArray, isObject, refName } from "./refs"

export type SchemaMode = "request" | "response"

const MAX_DEPTH = 8
// A schema with thousands of nested fields would bloat every page; past this the tree stops expanding.
const NODE_BUDGET = 1500

interface Ctx {
	resolver: RefResolver
	mode: SchemaMode
	budget: { left: number }
}

export function buildSchemaTree(
	schema: unknown,
	resolver: RefResolver,
	mode: SchemaMode,
): SchemaNode {
	return build(schema, { resolver, mode, budget: { left: NODE_BUDGET } }, 0, [])
}

/** Inlines $ref and allOf so callers read one flat schema object. */
export function flattenSchema(
	schema: unknown,
	resolver: RefResolver,
	stack: string[] = [],
): { schema: JsonObject; circularRef?: string; refLabel?: string } {
	if (!isObject(schema)) return { schema: {} }
	if (typeof schema.$ref === "string") {
		const ref = schema.$ref
		if (stack.includes(ref)) return { schema: {}, circularRef: ref }
		const target = resolver.lookup(ref)
		// 3.1 allows siblings next to $ref; they override the target.
		const { $ref: _ref, ...siblings } = schema
		const inner = flattenSchema(target, resolver, [...stack, ref])
		return {
			...inner,
			schema: { ...inner.schema, ...siblings },
			refLabel: refName(ref),
		}
	}
	if (Array.isArray(schema.allOf) && schema.allOf.length > 0) {
		const { allOf, ...rest } = schema
		let merged: JsonObject = {}
		let refLabel: string | undefined
		for (const part of allOf as unknown[]) {
			const flat = flattenSchema(part, resolver, stack)
			refLabel ??= flat.refLabel
			merged = mergeSchemas(merged, flat.schema)
		}
		return { schema: mergeSchemas(merged, rest), refLabel }
	}
	return { schema }
}

function mergeSchemas(a: JsonObject, b: JsonObject): JsonObject {
	const out: JsonObject = { ...a, ...b }
	if (isObject(a.properties) || isObject(b.properties)) {
		out.properties = {
			...(isObject(a.properties) ? a.properties : {}),
			...(isObject(b.properties) ? b.properties : {}),
		}
	}
	const required = [...asArray(a.required), ...asArray(b.required)]
	if (required.length > 0) out.required = [...new Set(required)]
	if (!out.type && (a.type || b.type)) out.type = a.type ?? b.type
	return out
}

function build(
	raw: unknown,
	ctx: Ctx,
	depth: number,
	stack: string[],
	name?: string,
	required?: boolean,
): SchemaNode {
	ctx.budget.left--
	const ownRef = isObject(raw) && typeof raw.$ref === "string" ? raw.$ref : null
	const { schema, circularRef, refLabel } = flattenSchema(
		raw,
		ctx.resolver,
		stack,
	)
	const nextStack = ownRef ? [...stack, ownRef] : stack
	const node: SchemaNode = { type: "any" }
	if (name !== undefined) node.name = name
	if (required) node.required = true
	if (circularRef) {
		node.type = refName(circularRef)
		node.circular = true
		return node
	}

	const { types, nullable } = readTypes(schema)
	if (nullable) node.nullable = true
	if (typeof schema.description === "string" && schema.description.trim()) {
		node.description = schema.description.trim()
	}
	if (typeof schema.format === "string") node.format = schema.format
	if (schema.deprecated === true) node.deprecated = true
	if (schema.readOnly === true) node.readOnly = true
	if (schema.writeOnly === true) node.writeOnly = true
	if (schema.default !== undefined) node.defaultValue = display(schema.default)
	const enumValues = Array.isArray(schema.enum)
		? schema.enum.filter((v) => v !== null)
		: schema.const !== undefined
			? [schema.const]
			: undefined
	if (enumValues && enumValues.length > 0) {
		node.enumValues = enumValues.map(display)
	}

	const variantKey = Array.isArray(schema.oneOf)
		? "oneOf"
		: Array.isArray(schema.anyOf)
			? "anyOf"
			: null
	if (variantKey) {
		const parts = (schema[variantKey] as unknown[]).filter(
			(part) => !isNullSchema(part, ctx.resolver),
		)
		if (parts.length < (schema[variantKey] as unknown[]).length) {
			node.nullable = true
		}
		// `anyOf: [X, null]` is how 3.1 writes a nullable X.
		if (parts.length === 1) {
			const inner = build(parts[0], ctx, depth, nextStack, name, required)
			return {
				...inner,
				nullable: true,
				description: inner.description ?? node.description,
			}
		}
		node.type = variantKey === "oneOf" ? "one of" : "any of"
		if (depth < MAX_DEPTH && ctx.budget.left > 0) {
			node.variants = parts.map((part, index) => {
				const child = build(part, ctx, depth + 1, nextStack)
				return {
					label: variantLabel(part, child, ctx.resolver, index),
					node: child,
				}
			})
		}
		return node
	}

	const type = types[0] ?? inferType(schema)
	if (type === "array") {
		const items = flattenSchema(schema.items, ctx.resolver, nextStack)
		const itemTypes = readTypes(items.schema).types
		const itemType =
			itemTypes[0] ?? inferType(items.schema) ?? items.refLabel ?? "any"
		node.type = `${itemType === "object" && items.refLabel ? items.refLabel : itemType}[]`
		if (
			isObject(schema.items) &&
			depth < MAX_DEPTH &&
			ctx.budget.left > 0 &&
			(itemType === "object" || hasVariants(items.schema))
		) {
			const child = build(schema.items, ctx, depth + 1, nextStack)
			if (child.children) node.children = child.children
			if (child.variants) node.variants = child.variants
			if (child.circular) node.circular = true
		}
		const itemEnum = Array.isArray(items.schema.enum)
			? items.schema.enum.filter((v) => v !== null)
			: undefined
		if (!node.enumValues && itemEnum?.length) {
			node.enumValues = itemEnum.map(display)
		}
		return node
	}

	if (type === "object") {
		node.type = refLabel && depth > 0 ? refLabel : "object"
		const properties = isObject(schema.properties) ? schema.properties : {}
		const requiredSet = new Set(
			Array.isArray(schema.required) ? (schema.required as string[]) : [],
		)
		if (depth < MAX_DEPTH && ctx.budget.left > 0) {
			const children: SchemaNode[] = []
			for (const [key, value] of Object.entries(properties)) {
				const flat = flattenSchema(value, ctx.resolver, nextStack).schema
				if (ctx.mode === "request" && flat.readOnly === true) continue
				if (ctx.mode === "response" && flat.writeOnly === true) continue
				children.push(
					build(value, ctx, depth + 1, nextStack, key, requiredSet.has(key)),
				)
			}
			if (isObject(schema.additionalProperties)) {
				const value = build(
					schema.additionalProperties,
					ctx,
					depth + 1,
					nextStack,
					"{key}",
				)
				if (children.length === 0) node.type = `map of ${value.type}`
				else children.push(value)
				if (value.children && children.length === 0) {
					node.children = value.children
				}
			}
			if (children.length > 0) node.children = children
		}
		return node
	}

	node.type = type ?? refLabel ?? "any"
	return node
}

function readTypes(schema: JsonObject): { types: string[]; nullable: boolean } {
	const raw = schema.type
	const list = Array.isArray(raw)
		? raw.filter((t): t is string => typeof t === "string")
		: typeof raw === "string"
			? [raw]
			: []
	return {
		types: list.filter((t) => t !== "null"),
		nullable: list.includes("null") || schema.nullable === true,
	}
}

function inferType(schema: JsonObject): string | undefined {
	if (isObject(schema.properties) || isObject(schema.additionalProperties)) {
		return "object"
	}
	if (schema.items !== undefined) return "array"
	const first =
		asArray(schema.enum).find((v) => v !== null) ??
		(schema.const === null ? undefined : schema.const)
	if (first !== undefined) return typeof first
	return undefined
}

const SCALAR_TYPES = new Set(["string", "integer", "number", "boolean"])

/** The type as readers expect it in a reference: `enum<string>`, `string<date-time>`, `enum<string>[]`, `string | null`. */
export function schemaTypeLabel(
	node: Pick<SchemaNode, "type" | "format" | "enumValues" | "nullable">,
): string {
	const isArray = node.type.endsWith("[]")
	const base = isArray ? node.type.slice(0, -2) : node.type
	let label = base
	if (SCALAR_TYPES.has(base)) {
		if (node.enumValues?.length) label = `enum<${base}>`
		else if (node.format && !isArray) label = `${base}<${node.format}>`
	}
	if (isArray) label += "[]"
	return node.nullable ? `${label} | null` : label
}

function hasVariants(schema: JsonObject): boolean {
	return Array.isArray(schema.oneOf) || Array.isArray(schema.anyOf)
}

function isNullSchema(part: unknown, resolver: RefResolver): boolean {
	const { schema } = flattenSchema(part, resolver)
	const { types, nullable } = readTypes(schema)
	return nullable && types.length === 0 && !isObject(schema.properties)
}

function variantLabel(
	part: unknown,
	node: SchemaNode,
	resolver: RefResolver,
	index: number,
): string {
	const flat = flattenSchema(part, resolver)
	if (typeof flat.schema.title === "string") return flat.schema.title
	if (flat.refLabel) return flat.refLabel
	if (node.type && node.type !== "object" && node.type !== "any") {
		return node.type
	}
	return `Option ${index + 1}`
}

function display(value: unknown): string {
	return typeof value === "string" ? value : JSON.stringify(value)
}
